/**
 * Mount list for the editor preview: the selected chapter and everything after
 * it, in project.json order.
 *
 * The player takes its `chapters` input as a set and plays whatever it keeps in
 * project.json order, so the slice has to be taken in that order too. Slicing a
 * differently ordered list (e.g. the config's own chapter order) leaves the
 * chapters preceding the selected one in the set, and playback starts at one of
 * those instead of at the selection.
 */
export function chaptersToMount(allProjectChapters: readonly string[], selectedSlug?: string): string[] {
	const startIdx = selectedSlug ? allProjectChapters.indexOf(selectedSlug) : 0;
	return allProjectChapters.slice(startIdx >= 0 ? startIdx : 0);
}

/**
 * The chapter the player would actually start on, or `-1` when none would
 * (DEV-833).
 *
 * Mirrors the player's `getAvailableChapter()`: it scans **forward** from the
 * start of the list it is given, which for the editor preview is the slice
 * `chaptersToMount()` produces — hence the scan from `from` first, then the
 * wrap to the top for the case where the selection is stranded at the end.
 *
 * `-1` is the state that has to be handled rather than mounted: the player
 * cannot initialise with nothing to play, and since `initFailed` does not
 * exist in 1.6.214 it hangs on "Initialising player…" instead of reporting it.
 */
export function firstAvailableChapterIndex(
	chapters: readonly { attributes?: Record<string, unknown> }[],
	argsMap: Record<string, string | number | boolean>,
	from: number,
	matches: (attributes: Record<string, unknown> | undefined, args: Record<string, string | number | boolean>) => boolean,
): number {
	const start = Math.min(Math.max(from, 0), chapters.length);
	for (let i = start; i < chapters.length; i++) if (matches(chapters[i].attributes, argsMap)) return i;
	for (let i = 0; i < start; i++) if (matches(chapters[i].attributes, argsMap)) return i;
	return -1;
}
